import React, { useEffect, useState } from 'react';
import {
  clientGateRequirement,
  subscribeToClientGate,
  type ClientGateRequirement,
} from '@/services/clientGate';

/**
 * The screen a build sees once the server has refused it, and the wrapper that
 * puts it there.
 *
 * Mounted above <App /> in main.tsx, outside the router and above every
 * provider, with no API, auth or theme dependency. A block screen that needs
 * the app to work in order to display is useless in exactly the situation it
 * exists for, so the theme is read straight off the document element — the
 * class ThemeProvider sets — rather than through its context.
 *
 * Terminal by design: no dismiss button. A dismissable upgrade prompt on a
 * client that genuinely cannot load a book only produces a second, more
 * confusing failure a tap later.
 */

const isDarkMode = (): boolean => {
  try {
    return document.documentElement.classList.contains('dark');
  } catch {
    return false;
  }
};

const UpgradeRequiredScreen: React.FC<{ requirement: ClientGateRequirement }> = ({
  requirement,
}) => {
  const dark = isDarkMode();

  return (
    <div
      className={`min-h-screen w-full flex items-center justify-center px-6 ${
        dark ? 'bg-gray-900 text-gray-100' : 'bg-white text-gray-900'
      }`}
      style={{
        paddingTop: 'calc(env(safe-area-inset-top) + 1.5rem)',
        paddingBottom: 'calc(env(safe-area-inset-bottom) + 1.5rem)',
      }}
      role="alert"
      aria-live="assertive"
    >
      <div className="flex flex-col items-center text-center max-w-sm">
        <h1 className="text-2xl font-semibold mb-3">Update Aleph</h1>
        <p className={`text-base leading-relaxed mb-8 ${dark ? 'text-gray-300' : 'text-gray-600'}`}>
          {requirement.message}
        </p>
        <a
          href={requirement.storeUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="w-full rounded-full bg-blue-600 px-6 py-3 text-white text-base font-medium active:bg-blue-700"
        >
          Open the App Store
        </a>
        {/*
          The one case that deserves better than a dead end: if a future build
          raises the iOS deployment target, a user on an older device gets an
          app that refuses to run and an App Store that will not offer them the
          version it demands. Naming the build here at least gives a support
          conversation somewhere to start. If that case ever becomes real, this
          needs copy that says so and a way to reach us — not a screen telling
          someone to do something impossible.
        */}
        <p className={`mt-6 text-xs ${dark ? 'text-gray-500' : 'text-gray-400'}`}>
          {requirement.minBuild === null
            ? `Build ${requirement.yourBuild}`
            : `Build ${requirement.yourBuild} · requires ${requirement.minBuild} or later`}
        </p>
      </div>
    </div>
  );
};

const ClientUpgradeGate: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [requirement, setRequirement] = useState<ClientGateRequirement | null>(
    clientGateRequirement,
  );

  useEffect(() => subscribeToClientGate(setRequirement), []);

  if (requirement) {
    return <UpgradeRequiredScreen requirement={requirement} />;
  }
  return <>{children}</>;
};

export default ClientUpgradeGate;
